import { WyscoutPlayerFixture } from '@/services/api/wyscout';

export type WyscoutParticipationFilter = 'all' | WyscoutPlayerFixture['participationStatus'];
export type WyscoutStatisticsFilter = WyscoutPlayerFixture['statsStatus'];
export type WyscoutCardFilter = 'all' | 'any' | 'yellow' | 'red';

export interface WyscoutMatchFilters {
  participation: WyscoutParticipationFilter;
  minMinutes: number | null;
  scored: boolean;
  assisted: boolean;
  clubIds: number[];
  venues: Array<Exclude<WyscoutPlayerFixture['venue'], null>>;
  results: Array<Exclude<WyscoutPlayerFixture['result'], null>>;
  opponent: string;
  positions: string[];
  seasonIds: number[];
  competitionIds: number[];
  dateFrom: string | null;
  dateTo: string | null;
  cards: WyscoutCardFilter;
  statsStatuses: WyscoutStatisticsFilter[];
}

export const defaultWyscoutMatchFilters: WyscoutMatchFilters = {
  participation: 'all',
  minMinutes: null,
  scored: false,
  assisted: false,
  clubIds: [],
  venues: [],
  results: [],
  opponent: '',
  positions: [],
  seasonIds: [],
  competitionIds: [],
  dateFrom: null,
  dateTo: null,
  cards: 'all',
  statsStatuses: [],
};

export const updateWyscoutMatchFilters = (
  current: WyscoutMatchFilters,
  patch: Partial<WyscoutMatchFilters>,
): WyscoutMatchFilters => {
  if (patch.participation === 'did_not_play') {
    return {
      ...current,
      ...patch,
      participation: 'did_not_play',
      minMinutes: null,
      scored: false,
      assisted: false,
      positions: [],
      cards: 'all',
    };
  }

  const nextPatch = { ...patch };
  if (nextPatch.minMinutes !== undefined && nextPatch.minMinutes !== null) {
    if (!Number.isInteger(nextPatch.minMinutes) || nextPatch.minMinutes < 0 || nextPatch.minMinutes > 120) {
      nextPatch.minMinutes = null;
    }
  }

  const performanceFilterApplied =
    (nextPatch.minMinutes !== undefined && nextPatch.minMinutes !== null)
    || nextPatch.scored === true
    || nextPatch.assisted === true
    || Boolean(nextPatch.positions?.length)
    || (nextPatch.cards !== undefined && nextPatch.cards !== 'all');

  return {
    ...current,
    ...nextPatch,
    participation: performanceFilterApplied ? 'played' : nextPatch.participation ?? current.participation,
  };
};

export const filterWyscoutFixtures = (
  fixtures: WyscoutPlayerFixture[],
  filters: WyscoutMatchFilters,
): WyscoutPlayerFixture[] => {
  const opponent = filters.opponent.trim().toLowerCase();

  return fixtures.filter(fixture => {
    if (filters.participation !== 'all' && fixture.participationStatus !== filters.participation) return false;
    if (filters.minMinutes !== null && (fixture.minutes === null || fixture.minutes < filters.minMinutes)) return false;
    if (filters.scored && (fixture.goals === null || fixture.goals < 1)) return false;
    if (filters.assisted && (fixture.assists === null || fixture.assists < 1)) return false;
    if (filters.clubIds.length && (fixture.playerTeamId === null || !filters.clubIds.includes(fixture.playerTeamId))) return false;
    if (filters.venues.length && (fixture.venue === null || !filters.venues.includes(fixture.venue))) return false;
    if (filters.results.length && (fixture.result === null || !filters.results.includes(fixture.result))) return false;
    if (opponent && !fixture.opponentTeamName?.toLowerCase().includes(opponent)) return false;
    if (filters.positions.length && (fixture.position === null || !filters.positions.includes(fixture.position))) return false;
    if (filters.seasonIds.length && !filters.seasonIds.includes(fixture.seasonId)) return false;
    if (filters.competitionIds.length && !filters.competitionIds.includes(fixture.competitionId)) return false;

    const fixtureDate = fixture.dateUtc.slice(0, 10);
    if (filters.dateFrom && fixtureDate < filters.dateFrom) return false;
    if (filters.dateTo && fixtureDate > filters.dateTo) return false;

    if (filters.cards === 'yellow' && (fixture.yellowCards === null || fixture.yellowCards < 1)) return false;
    if (filters.cards === 'red' && (fixture.redCards === null || fixture.redCards < 1)) return false;
    if (filters.cards === 'any' && !(
      (fixture.yellowCards !== null && fixture.yellowCards > 0)
      || (fixture.redCards !== null && fixture.redCards > 0)
    )) return false;

    if (filters.statsStatuses.length && !filters.statsStatuses.includes(fixture.statsStatus)) return false;
    return true;
  });
};

const aggregateMetricNames = [
  'minutes',
  'goals',
  'assists',
  'shots',
  'shotsOnTarget',
  'xg',
  'xgAssist',
  'passes',
  'successfulPasses',
  'duels',
  'duelsWon',
  'defensiveActions',
  'interceptions',
  'recoveries',
  'yellowCards',
  'redCards',
] as const;

export type WyscoutAggregateMetric = typeof aggregateMetricNames[number];

export interface WyscoutFixtureAggregates extends Record<WyscoutAggregateMetric, number | null> {
  fixturesShown: number;
  appearances: number;
  availableAppearances: number;
  measured: Record<WyscoutAggregateMetric, number>;
}

export const aggregateWyscoutFixtures = (fixtures: WyscoutPlayerFixture[]): WyscoutFixtureAggregates => {
  const measured = Object.fromEntries(aggregateMetricNames.map(metric => [metric, 0])) as Record<WyscoutAggregateMetric, number>;
  const totals = Object.fromEntries(aggregateMetricNames.map(metric => [metric, null])) as Record<WyscoutAggregateMetric, number | null>;
  let appearances = 0;
  let availableAppearances = 0;

  fixtures.forEach(fixture => {
    if (fixture.participationStatus !== 'played') return;
    appearances += 1;
    if (fixture.statsStatus !== 'available') return;
    availableAppearances += 1;

    aggregateMetricNames.forEach(metric => {
      const value = fixture[metric];
      if (value === null) return;
      totals[metric] = (totals[metric] ?? 0) + value;
      measured[metric] += 1;
    });
  });

  return {
    fixturesShown: fixtures.length,
    appearances,
    availableAppearances,
    ...totals,
    measured,
  };
};

export const activeWyscoutFilterCount = (filters: WyscoutMatchFilters): number => {
  const hasPerformanceFilter = (filters.minMinutes !== null)
    || filters.scored
    || filters.assisted
    || filters.positions.length > 0
    || filters.cards !== 'all';

  return [
    filters.participation !== 'all' && !hasPerformanceFilter,
    filters.minMinutes !== null,
    filters.scored,
    filters.assisted,
    filters.clubIds.length > 0,
    filters.venues.length > 0,
    filters.results.length > 0,
    filters.opponent.trim().length > 0,
    filters.positions.length > 0,
    filters.seasonIds.length > 0,
    filters.competitionIds.length > 0,
    Boolean(filters.dateFrom || filters.dateTo),
    filters.cards !== 'all',
    filters.statsStatuses.length > 0,
  ].filter(Boolean).length;
};

export const activeWyscoutAdvancedFilterCount = (filters: WyscoutMatchFilters): number => [
  filters.clubIds.length > 0,
  filters.venues.length > 0,
  filters.results.length > 0,
  filters.opponent.trim().length > 0,
  filters.positions.length > 0,
  filters.seasonIds.length > 0,
  filters.competitionIds.length > 0,
  Boolean(filters.dateFrom || filters.dateTo),
  filters.cards !== 'all',
  filters.statsStatuses.length > 0,
].filter(Boolean).length;
